////////////////////////////////////////////
// PAGE SHELL — SHARED CHROME AROUND EVERY ROUTE
////////////////////////////////////////////
//
// RENDERS THE TOP NAV + FOOTER AROUND THE ROUTED PAGE AND OWNS THE SPA
// EQUIVALENT OF "ONE PAGE LOAD": EVERY ROUTE NAVIGATION MINTS A FRESH plid
// AND RE-FIRES THE PAGEVIEW + SESSION BEACON (lib/log.ts newPageLoad), SO
// THE ADMIN DASHBOARDS SEE THE SAME NESTING THE VANILLA MPA PRODUCED.
//
// THE ONE TopNav RENDERS ON EVERY PAGE — HOME GETS THE TRANSPARENT overlay
// VARIANT ABOVE ITS HERO VIDEO; EVERYTHING ELSE GETS THE STICKY solid ONE.
import { useEffect, useRef } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { TopNav, type NavVariant } from "@/app/layout/TopNav";
import { Footer } from "@/app/layout/Footer";
import { WalletConnectModal } from "@/app/layout/WalletConnectModal";
import { DlToast } from "@/app/layout/DlToast";
import { ConnectProvider } from "@/app/providers/ConnectProvider";
import { useNetwork } from "@/config/useNetwork";
import * as log from "@/lib/log";
import { useSeo } from "@/lib/seo";

// PER-ROUTE CHROME — NAV VARIANT, FOOTER, AND CONNECT-BUTTON VISIBILITY
function chromeFor(pathname: string, hasLaunch: boolean): { nav: NavVariant; footer: boolean; connect: boolean } {

  // HOME — SAME NAV, TRANSPARENT OVER THE FULL-BLEED HERO; NO FOOTER
  if (pathname === "/" && !hasLaunch) return { nav: "overlay", footer: false, connect: true };
  // ADMIN — THE PORTAL SESSION IS SIGN-IN-WITH-WALLET, NOT THE CREATE-FLOW
  // CONNECT, SO THE CONNECT BUTTON WOULD ONLY CONFUSE
  if (pathname.startsWith("/admin")) return { nav: "solid", footer: true, connect: false };
  // LOGIN — THE PAGE BODY OWNS ITS OWN CONNECT/SIGN-IN FLOW
  if (pathname.startsWith("/login")) return { nav: "solid", footer: true, connect: false };
  return { nav: "solid", footer: true, connect: true };
}

export function PageShell() {

  const location = useLocation();
  const [search, setSearchParams] = useSearchParams();
  const net = useNetwork();

  // LEGACY ?chain= LINKS — RESOLVE FINE (config/network READS THEM FOREVER)
  // BUT THE URL CANONICALIZES TO THE SINGLE ?net= FORM SO COPIED LINKS
  // CONVERGE ON THE NEW CONTRACT
  useEffect(() => {

    if (!search.get("chain")) return;

    const p = new URLSearchParams(search);
    p.delete("chain");
    if (net.activeNet === "mainnet") p.delete("net");
    else p.set("net", net.activeNet);
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // ROUTE NAVIGATION -> NEW PAGE LOAD (SKIP THE FIRST — log.arm() COVERED IT)
  const firstRef = useRef(true);
  useEffect(() => {

    if (firstRef.current) {

      firstRef.current = false;
      return;
    }
    log.newPageLoad(String(net.activeNet));
    // KEY ON THE FULL URL — PATH OR QUERY CHANGE IS A NAVIGATION
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const launchKey = search.get("launch");
  useSeo(location.pathname, launchKey);

  const chrome = chromeFor(location.pathname, !!launchKey);

  return (
    // CONNECT PROVIDER WRAPS EVERY ROUTE — INSIDE THE ROUTER (IT READS
    // ?net/?chain) AND STABLE ACROSS NAVIGATION (LAYOUT ROUTE), SO CONNECT
    // STATE + THE SHARED MODAL PERSIST AS THE Outlet SWAPS PAGES.
    <ConnectProvider>
      <TopNav variant={chrome.nav} showConnect={chrome.connect} />
      {/* MOBILE BOTTOM-BAR CLEARANCE — THE FIXED TAB BAR OWNS THE LAST
          54px (+ SAFE AREA); HOME (footer:false) SIZES ITS OWN HERO */}
      <div className={chrome.footer ? "pb-[calc(54px+env(safe-area-inset-bottom))] lg:pb-0" : undefined}>
        <Outlet />
        {chrome.footer && <Footer />}
      </div>
      <WalletConnectModal />
      <DlToast />
    </ConnectProvider>
  );
}
